import { RefObject, useEffect, useState } from 'react';

type SdkSlotOptions = {
  /** SDK elements that get a slot appended as their last child */
  selector: string;
  className: string;
};

/**
 * @description Appends a slot to each matching SDK element for the layout to portal into, and
 * replaces slots the SDK drops.
 * @returns the slots currently in the panel
 */
export const useSdkSlots = (
  panelRef: RefObject<HTMLElement | null>,
  { selector, className }: SdkSlotOptions,
) => {
  const [slots, setSlots] = useState<HTMLElement[]>([]);

  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) return;

    const slotsByHost = new Map<Element, HTMLElement>();

    const scan = () => {
      let hasChanged = false;
      slotsByHost.forEach((slot, host) => {
        if (panel.contains(slot)) return;
        slotsByHost.delete(host);
        hasChanged = true;
      });
      panel.querySelectorAll(selector).forEach((host) => {
        if (slotsByHost.has(host)) return;
        const slot = document.createElement('div');
        slot.className = className;
        host.append(slot);
        slotsByHost.set(host, slot);
        hasChanged = true;
      });
      if (hasChanged) setSlots([...slotsByHost.values()]);
    };

    const observer = new MutationObserver(scan);
    observer.observe(panel, { childList: true, subtree: true });
    scan();

    return () => {
      observer.disconnect();
      slotsByHost.forEach((slot) => slot.remove());
    };
  }, [panelRef, selector, className]);

  return slots;
};
